import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Bell, CheckCircle2, ChevronRight, Database, Info,
  KeyRound, Lock, Mail, Shield, User,
} from 'lucide-react';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import {
  changeEmail, changePassword, endMaintenance, getMaintenanceStatus,
  getPreferences, startMaintenance, updatePreferences,
} from '../api';

const StatusPill = ({ children, tone = 'neutral' }) => {
  const tones = {
    success: { bg: '#F0FDF4', text: '#166534' },
    warning: { bg: '#FFFBEB', text: '#92400E' },
    danger: { bg: '#FEF2F2', text: '#991B1B' },
    info: { bg: '#EFF6FF', text: '#1D4ED8' },
    neutral: { bg: '#F3F4F6', text: '#374151' },
  };
  const c = tones[tone] || tones.neutral;
  return <span style={{ background: c.bg, color: c.text, padding: '3px 9px', borderRadius: 99, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' }}>{children}</span>;
};

const Toggle = ({ checked, disabled, onChange, theme }) => (
  <button type="button" onClick={() => onChange(!checked)} disabled={disabled} aria-pressed={checked} style={{
    width: 42, height: 24, borderRadius: 99, border: `1px solid ${checked ? '#16A34A' : theme.border}`,
    cursor: disabled ? 'not-allowed' : 'pointer', background: checked ? '#16A34A' : theme.bgInput,
    position: 'relative', transition: 'background 0.2s, border 0.2s', flexShrink: 0,
  }}>
    <span style={{
      width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute',
      top: 2, left: checked ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(15,23,42,0.25)',
    }} />
  </button>
);

const Panel = ({ children, theme }) => (
  <section style={{
    background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 10,
    boxShadow: theme.shadowSm, padding: 20,
  }}>
    {children}
  </section>
);

const SectionHeader = ({ eyebrow, title, description, action, theme }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
    <div>
      <p style={{ margin: '0 0 5px', fontSize: 11, fontWeight: 800, color: '#3B82F6', textTransform: 'uppercase' }}>{eyebrow}</p>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 750, color: theme.textPrimary }}>{title}</h2>
      {description && <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary, lineHeight: 1.5 }}>{description}</p>}
    </div>
    {action}
  </div>
);

const FieldRow = ({ icon: Icon, label, description, value, action, theme }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: action ? '1fr auto' : '1fr', gap: 16,
    alignItems: 'center', padding: '15px 0', borderBottom: `1px solid ${theme.border}`,
  }}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
      {Icon && (
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: theme.bgInput,
          border: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={15} color={theme.textSecondary} />
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, color: theme.textPrimary, fontWeight: 700 }}>{label}</p>
        {description && <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textTertiary, lineHeight: 1.45 }}>{description}</p>}
        {value && <div style={{ margin: '6px 0 0', fontSize: 13, color: theme.textSecondary, wordBreak: 'break-word' }}>{value}</div>}
      </div>
    </div>
    {action}
  </div>
);

const Settings = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [active, setActive] = useState('account');
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceReason, setMaintenanceReason] = useState('');
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState('');
  const [regulatoryAlerts, setRegulatoryAlerts] = useState(false);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefSaved, setPrefSaved] = useState(false);

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);

  const navItems = useMemo(() => ([
    { id: 'account', label: 'Account', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    ...(user.role === 'ADMIN' ? [{ id: 'admin', label: 'Admin Controls', icon: Shield }] : []),
    { id: 'compliance', label: 'Compliance', icon: Database },
    { id: 'platform', label: 'Platform', icon: Info },
  ]), [user.role]);

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${theme.border}`,
    background: theme.bgInput, color: theme.textPrimary, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  const primaryBtn = {
    border: 'none', borderRadius: 8, background: '#3B82F6', color: 'white', cursor: 'pointer',
    fontSize: 13, fontWeight: 800, padding: '9px 15px', display: 'inline-flex', alignItems: 'center', gap: 7,
  };

  const secondaryBtn = {
    border: `1px solid ${theme.border}`, borderRadius: 8, background: theme.bgInput, color: theme.textSecondary,
    cursor: 'pointer', fontSize: 12, fontWeight: 800, padding: '8px 12px',
  };

  const fetchMaintenanceStatus = async () => {
    try {
      const res = await getMaintenanceStatus();
      setMaintenance(res.data);
    } catch (err) {
      console.error('Maintenance status error:', err);
    }
  };

  useEffect(() => {
    if (user.role === 'ADMIN') fetchMaintenanceStatus();
    getPreferences()
      .then(res => setRegulatoryAlerts(Boolean(res.data.regulatory_alerts_email)))
      .catch(() => {});
  }, [user.role]);

  const handleToggleRegulatoryAlerts = async (val) => {
    setPrefSaving(true);
    setPrefSaved(false);
    try {
      await updatePreferences({ regulatory_alerts_email: val });
      setRegulatoryAlerts(val);
      setPrefSaved(true);
      setTimeout(() => setPrefSaved(false), 2400);
    } catch (err) {
      console.error('Preference save error:', err);
    } finally {
      setPrefSaving(false);
    }
  };

  const handleMaintenanceStart = async () => {
    if (!maintenanceReason.trim()) {
      setMaintenanceError('A reason is required before maintenance mode can be activated.');
      return;
    }
    setMaintenanceLoading(true);
    setMaintenanceError('');
    try {
      await startMaintenance(maintenanceReason);
      await fetchMaintenanceStatus();
      setMaintenanceReason('');
    } catch (err) {
      setMaintenanceError(err.response?.data?.error || err.message);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleMaintenanceEnd = async () => {
    setMaintenanceLoading(true);
    try {
      await endMaintenance();
      await fetchMaintenanceStatus();
    } catch (err) {
      console.error('Maintenance end error:', err);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (pwForm.new !== pwForm.confirm) return setPwMsg({ type: 'error', text: 'New passwords do not match.' });
    if (pwForm.new.length < 8) return setPwMsg({ type: 'error', text: 'Password must be at least 8 characters.' });

    setPwLoading(true);
    setPwMsg(null);
    try {
      await changePassword({ current_password: pwForm.current, new_password: pwForm.new });
      setPwMsg({ type: 'success', text: 'Password updated successfully.' });
      setPwForm({ current: '', new: '', confirm: '' });
      setTimeout(() => { setShowChangePassword(false); setPwMsg(null); }, 1800);
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update password.' });
    } finally {
      setPwLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!emailForm.new_email || !emailForm.password) return setEmailMsg({ type: 'error', text: 'All fields are required.' });

    setEmailLoading(true);
    setEmailMsg(null);
    try {
      await changeEmail({ new_email: emailForm.new_email, password: emailForm.password });
      setEmailMsg({ type: 'success', text: 'Email updated. Please log in again.' });
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setEmailMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update email.' });
    } finally {
      setEmailLoading(false);
    }
  };

  const msg = (m) => m && (
    <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: m.type === 'error' ? '#DC2626' : '#166534' }}>
      {m.text}
    </p>
  );

  const content = {
    account: (
      <Panel theme={theme}>
        <SectionHeader eyebrow="Account" title="Profile" description="The identity and access details attached to your RegShield workspace." theme={theme} />
        <FieldRow icon={Mail} label="Email address" value={user.email || '-'} theme={theme}
          action={<button style={secondaryBtn} onClick={() => { setShowChangeEmail(v => !v); setEmailMsg(null); }}>{showChangeEmail ? 'Cancel' : 'Change'}</button>}
        />
        {showChangeEmail && (
          <div style={{ display: 'grid', gap: 10, padding: '0 0 15px 44px', borderBottom: `1px solid ${theme.border}` }}>
            <input type="email" placeholder="New email address" value={emailForm.new_email} onChange={e => setEmailForm(f => ({ ...f, new_email: e.target.value }))} style={inputStyle} />
            <input type="password" placeholder="Confirm your password" value={emailForm.password} onChange={e => setEmailForm(f => ({ ...f, password: e.target.value }))} style={inputStyle} />
            {msg(emailMsg)}
            <button onClick={handleChangeEmail} disabled={emailLoading} style={{ ...primaryBtn, width: 'fit-content', cursor: emailLoading ? 'not-allowed' : 'pointer' }}>
              {emailLoading ? 'Updating...' : 'Update Email'}
            </button>
          </div>
        )}
        <FieldRow icon={Shield} label="Role" value={user.role || '-'} theme={theme} />
        <FieldRow icon={KeyRound} label="User ID" description="Partial identifier shown for support and audit references." value={user.id ? `${user.id.slice(0, 8)}...` : '-'} theme={theme} />
        <FieldRow icon={Database} label="Fund manager scope" value={user.fund_manager_id ? `${user.fund_manager_id.slice(0, 8)}...` : 'All fund managers'} theme={theme} />
      </Panel>
    ),

    security: (
      <Panel theme={theme}>
        <SectionHeader eyebrow="Security" title="Authentication" description="Manage sign-in credentials and session controls." theme={theme} />
        <FieldRow icon={Lock} label="Password" description="Use a strong password that is unique to RegShield." theme={theme}
          action={<button style={secondaryBtn} onClick={() => { setShowChangePassword(v => !v); setPwMsg(null); }}>{showChangePassword ? 'Cancel' : 'Change'}</button>}
        />
        {showChangePassword && (
          <div style={{ display: 'grid', gap: 10, padding: '0 0 15px 44px', borderBottom: `1px solid ${theme.border}` }}>
            <input type="password" placeholder="Current password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} style={inputStyle} />
            <button onClick={async () => {
              try {
                await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/auth/forgot-password`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: user.email }),
                });
                setPwMsg({ type: 'success', text: `Password reset link sent to ${user.email}. You will be logged out.` });
                setTimeout(() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.href = '/login';
                }, 3000);
              } catch {
                setPwMsg({ type: 'error', text: 'Failed to send reset email.' });
              }
            }} style={{ background: 'none', border: 'none', color: '#3B82F6', cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: 0, justifySelf: 'start' }}>
              Forgot current password?
            </button>
            <input type="password" placeholder="New password (min 8 chars)" value={pwForm.new} onChange={e => setPwForm(f => ({ ...f, new: e.target.value }))} style={inputStyle} />
            <input type="password" placeholder="Confirm new password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} style={inputStyle} />
            {msg(pwMsg)}
            <button onClick={handleChangePassword} disabled={pwLoading} style={{ ...primaryBtn, width: 'fit-content', cursor: pwLoading ? 'not-allowed' : 'pointer' }}>
              {pwLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        )}
        <FieldRow icon={CheckCircle2} label="JWT session" description="Users are automatically logged out after the configured session window." value="30 days" theme={theme} />
        <FieldRow icon={KeyRound} label="Two-factor authentication" description="MFA will be available in a future release." value={<StatusPill>Not configured</StatusPill>} theme={theme} />
      </Panel>
    ),

    notifications: (
      <Panel theme={theme}>
        <SectionHeader
          eyebrow="Notifications"
          title="Alert Delivery"
          description="Choose which operational and regulatory events should trigger email alerts."
          theme={theme}
          action={prefSaving ? <StatusPill tone="info">Saving</StatusPill> : prefSaved ? <StatusPill tone="success">Saved</StatusPill> : null}
        />
        <FieldRow icon={Bell} label="Regulatory alerts" description="Email when new binding documents are detected by the scraper." theme={theme}
          action={<Toggle checked={regulatoryAlerts} disabled={prefSaving} onChange={handleToggleRegulatoryAlerts} theme={theme} />}
        />
        <FieldRow icon={AlertTriangle} label="DORA incidents" description="Sent when a new incident is detected or a reporting SLA is overdue." value={<StatusPill tone="info">Email</StatusPill>} theme={theme} />
        <FieldRow icon={Shield} label="Hash chain tamper alerts" description="Sent immediately if audit log tampering is detected." value={<StatusPill tone="info">Email</StatusPill>} theme={theme} />
      </Panel>
    ),

    admin: (
      <Panel theme={theme}>
        <SectionHeader
          eyebrow="Admin"
          title="Maintenance Mode"
          description="Temporary privileged access for controlled support and investigation workflows."
          theme={theme}
          action={maintenance?.active ? <StatusPill tone="warning">Active</StatusPill> : <StatusPill>Inactive</StatusPill>}
        />
        {maintenance?.active ? (
          <div>
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: 14, marginBottom: 14 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#92400E' }}>Full PII access is enabled</p>
              <p style={{ margin: '5px 0 0', fontSize: 12, color: '#92400E', lineHeight: 1.5 }}>
                Access expires at {new Date(maintenance.session?.expires_at).toLocaleTimeString()}. All access is recorded in the audit trail.
              </p>
            </div>
            <button onClick={handleMaintenanceEnd} disabled={maintenanceLoading} style={{ ...primaryBtn, background: '#DC2626', width: isMobile ? '100%' : 'fit-content' }}>
              {maintenanceLoading ? 'Ending...' : 'End Maintenance Mode'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            <input type="text" placeholder="Reason for privileged access" value={maintenanceReason} onChange={e => setMaintenanceReason(e.target.value)} style={inputStyle} />
            {maintenanceError && <p style={{ margin: 0, fontSize: 12, color: '#DC2626', fontWeight: 700 }}>{maintenanceError}</p>}
            <button onClick={handleMaintenanceStart} disabled={maintenanceLoading} style={{ ...primaryBtn, width: isMobile ? '100%' : 'fit-content' }}>
              {maintenanceLoading ? 'Activating...' : 'Activate Maintenance Mode'}
            </button>
          </div>
        )}
      </Panel>
    ),

    compliance: (
      <Panel theme={theme}>
        <SectionHeader eyebrow="Compliance" title="Audit Operations" description="Operational controls used for retention, monitoring, and regulatory evidence." theme={theme} />
        <FieldRow icon={Database} label="Audit log retention" description="Required by MiFID II and Luxembourg AML law." value="5 years" theme={theme} />
        <FieldRow icon={Shield} label="Hash chain verification" description="Automated tamper detection runs every night." value="Nightly at 02:00" theme={theme} />
        <FieldRow icon={Bell} label="Regulatory scraper" description="CSSF, ESMA, EBA, and Legilux sources." value="Daily at 06:00" theme={theme} />
        <FieldRow icon={AlertTriangle} label="DORA SLA monitoring" description="Checks for overdue CSSF notification deadlines." value="Every 30 minutes" theme={theme} />
      </Panel>
    ),

    platform: (
      <Panel theme={theme}>
        <SectionHeader eyebrow="Platform" title="Environment" description="Workspace metadata and operating context." theme={theme} />
        <FieldRow label="Platform" value="RegShield" theme={theme} />
        <FieldRow label="Version" value="v1.0.0" theme={theme} />
        <FieldRow label="Environment" value={<StatusPill tone="success">Production</StatusPill>} theme={theme} />
        <FieldRow label="Database" value="PostgreSQL 15" theme={theme} />
        <FieldRow label="AI Model" value="Claude Sonnet 4.6" theme={theme} />
        <FieldRow label="Jurisdiction" value="Luxembourg (CSSF)" theme={theme} />
        <FieldRow label="Data residency" description="GDPR compliant." value="EU (Luxembourg)" theme={theme} />
      </Panel>
    ),
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: theme.textTertiary, cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: 0, marginBottom: 10 }}>
            Back to Dashboard
          </button>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.textPrimary }}>Settings</h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary }}>
            Account, security, notifications, and governance controls.
          </p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
          border: `1px solid ${theme.border}`, borderRadius: 8, background: theme.bgCard, boxShadow: theme.shadowSm,
        }}>
          <CheckCircle2 size={15} color="#16A34A" />
          <span style={{ fontSize: 12, fontWeight: 800, color: theme.textSecondary }}>Workspace healthy</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '240px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
        <aside style={{
          background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 10, boxShadow: theme.shadowSm,
          padding: 8, position: isMobile ? 'static' : 'sticky', top: 16, display: isMobile ? 'flex' : 'block',
          gap: isMobile ? 6 : undefined, overflowX: isMobile ? 'auto' : undefined,
        }}>
          {!isMobile && (
            <div style={{ padding: '12px 10px 14px', borderBottom: `1px solid ${theme.border}`, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, background: '#EFF6FF', color: '#1D4ED8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900,
                }}>
                  {(user.email || 'U').slice(0, 1).toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, color: theme.textPrimary, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.email || 'User'}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textTertiary, fontWeight: 700 }}>{user.role || 'Member'}</p>
                </div>
              </div>
            </div>
          )}

          {navItems.map(item => {
            const Icon = item.icon;
            const selected = active === item.id;
            return (
              <button key={item.id} onClick={() => setActive(item.id)} style={{
                width: isMobile ? 'auto' : '100%', border: `1px solid ${selected ? '#BFDBFE' : 'transparent'}`,
                background: selected ? '#EFF6FF' : 'transparent', color: selected ? '#1D4ED8' : theme.textSecondary,
                borderRadius: 8, padding: '10px 10px', display: 'flex', alignItems: 'center', gap: 9,
                justifyContent: 'space-between', cursor: 'pointer', fontSize: 13, fontWeight: 800,
                textAlign: 'left', marginBottom: isMobile ? 0 : 2, flexShrink: 0,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <Icon size={15} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                </span>
                {!isMobile && <ChevronRight size={14} style={{ opacity: selected ? 1 : 0.35 }} />}
              </button>
            );
          })}
        </aside>

        <main style={{ minWidth: 0 }}>
          {content[active]}
        </main>
      </div>
    </div>
  );
};

export default Settings;